/**
 * addon-cards.js v2
 *
 * Handles standalone add-on group selectors on the product page.
 * Each addon_group block renders its own .addon-cards__group independently;
 * this script stitches them together at the form level.
 *
 * Architecture:
 * - One form submit interceptor per product form (registry prevents duplicates)
 * - Collects selected add-on variant IDs from ALL groups tied to that form
 * - Per-group radio-button click behaviour and label updates
 * - Info drawer open / close
 */
(function () {
  'use strict';

  // ── Global form interceptor registry ─────────────────────────────────────────
  // Keyed by form.getAttribute('id') — ensures we attach exactly one submit listener per form
  // even when multiple addon_group blocks are on the page.
  var interceptedForms = {};

  function ensureFormInterceptor(form) {
    if (interceptedForms[form.getAttribute('id')]) return;
    interceptedForms[form.getAttribute('id')] = true;

    form.addEventListener(
      'submit',
      function (event) {
        // Mirror ProductForm's fallback: on cart-page templates or page-type cart,
        // let the form submit normally (full-page redirect).
        if (document.body.classList.contains('template-cart')) return;
        if (typeof theme !== 'undefined' && theme.settings && theme.settings.cartType === 'page') return;

        // Collect variant IDs from ALL groups linked to this form
        var addonIds = [];
        document
          .querySelectorAll(
            '.addon-cards__group[data-form-id="' + form.getAttribute('id') + '"] .addon-card.is-selected:not([disabled])'
          )
          .forEach(function (card) {
            var vid = card.dataset.variantId;
            if (vid && vid !== '') addonIds.push(parseInt(vid, 10));
          });

        // No add-ons selected — let ProductForm handle normally
        if (addonIds.length === 0) return;

        var submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn && submitBtn.hasAttribute('aria-disabled')) return;

        // Guard: theme object must be available
        // NOTE: check BEFORE preventDefault so ProductForm can still handle it if theme isn't ready
        if (typeof theme === 'undefined' || !theme.routes || !theme.utils) {
          console.error('[addon-cards] theme object not ready — letting ProductForm handle');
          return;
        }

        // All guards passed — NOW take over the submission
        event.preventDefault();
        event.stopImmediatePropagation();

        // Main product variant
        var variantInput = form.querySelector('[name="id"]');
        if (!variantInput) return;
        var mainVariantId = parseInt(variantInput.value, 10);

        // Respect quantity selector
        var quantityInput = form.querySelector('[name="quantity"]');
        var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

        // Preserve custom line-item properties (e.g. _shipping)
        var mainItem = { id: mainVariantId, quantity: quantity };
        var shippingInput = form.querySelector('[name="properties[_shipping]"]');
        if (shippingInput && shippingInput.value) {
          mainItem.properties = { '_shipping': shippingInput.value };
        }

        // Build items array: main product first, then each selected add-on
        var items = [mainItem].concat(
          addonIds.map(function (id) {
            return { id: id, quantity: quantity };
          })
        );

        // Allow theme to bundle additional sections
        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        var body = JSON.stringify({
          items: items,
          sections: sectionsToBundle,
          sections_url: window.location.pathname
        });

        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', 'true');
          submitBtn.setAttribute('aria-busy', 'true');
        }

        fetch(
          theme.routes.cart_add_url,
          Object.assign({}, theme.utils.fetchConfig('javascript'), { body: body })
        )
          .then(function (response) { return response.json(); })
          .then(function (parsedState) {
            if (parsedState.status) {
              var errEl = form.querySelector('.product-form__error-message');
              if (errEl) {
                errEl.removeAttribute('hidden');
                errEl.textContent = parsedState.description;
              }
              document.dispatchEvent(
                new CustomEvent('ajaxProduct:error', {
                  detail: { errorMessage: parsedState.description }
                })
              );
              return;
            }

            return fetch(
              theme.routes.cart_url,
              Object.assign({}, theme.utils.fetchConfig('json', 'GET'))
            )
              .then(function (r) { return r.json(); })
              .then(function (cartJson) {
                cartJson['sections'] = parsedState['sections'];

                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                  source: 'addon-cards',
                  productVariantId: mainVariantId,
                  cart: cartJson
                });

                document.dispatchEvent(
                  new CustomEvent('ajaxProduct:added', {
                    detail: { product: parsedState }
                  })
                );

                var cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer) cartDrawer.show(submitBtn);
              });
          })
          .catch(function (err) {
            console.error('[addon-cards] Error adding to cart:', err);
          })
          .finally(function () {
            if (submitBtn) {
              submitBtn.removeAttribute('aria-busy');
              submitBtn.removeAttribute('aria-disabled');
            }
          });
      },
      { capture: true }
    );
  }

  // ── Per-group initialisation ──────────────────────────────────────────────────
  function initAddonGroup(groupEl) {
    if (groupEl.dataset.addonCardsInitialized) return;
    groupEl.dataset.addonCardsInitialized = 'true';

    var formId = groupEl.dataset.formId;
    var form   = document.getElementById(formId);
    if (!form) return;

    // Bind the form interceptor (once per form)
    ensureFormInterceptor(form);

    /** Sync the "Group: Selected option" label above the cards */
    function updateGroupLabel() {
      var selected  = groupEl.querySelector('.addon-card.is-selected');
      var labelSpan = groupEl.querySelector('.addon-cards__selected-label');
      if (selected && labelSpan) {
        labelSpan.textContent = ' ' + (selected.dataset.label || '');
      }
    }

    /** Radio-button selection within this group */
    function selectCard(card) {
      if (!card) return;
      groupEl.querySelectorAll('.addon-card').forEach(function (c) {
        c.classList.remove('is-selected');
        c.setAttribute('aria-pressed', 'false');
      });
      card.classList.add('is-selected');
      card.setAttribute('aria-pressed', 'true');
      updateGroupLabel();
    }

    // Initialise label on load
    updateGroupLabel();

    groupEl.querySelectorAll('.addon-card').forEach(function (card) {
      card.addEventListener('click', function () { selectCard(card); });
    });

    // ── Variant matching ────────────────────────────────────────────────────
    // Only present when at least one add-on in this group shares an option
    // with the parent product (e.g. both have "Color"). Universal add-ons
    // such as casters emit no map and are never touched here.
    var mapEl = groupEl.querySelector('.addon-cards__variant-map');
    if (!mapEl) return;

    var variantMap;
    try {
      variantMap = JSON.parse(mapEl.textContent);
    } catch (err) {
      console.error('[addon-cards] could not parse variant map', err);
      return;
    }

    function applyParentVariant(parentVariantId) {
      var slots = variantMap[String(parentVariantId)];
      if (!slots) return;

      Object.keys(slots).forEach(function (slot) {
        var card = groupEl.querySelector('.addon-card[data-slot="' + slot + '"]');
        if (!card) return;
        var data = slots[slot];

        card.dataset.variantId = data.id ? String(data.id) : '';

        if (data.available) {
          card.removeAttribute('disabled');
          card.removeAttribute('aria-disabled');
          card.classList.remove('addon-card--unavailable');
        } else {
          card.setAttribute('disabled', '');
          card.setAttribute('aria-disabled', 'true');
          card.classList.add('addon-card--unavailable');
          // The shopper had this add-on selected but it does not exist in the
          // newly chosen colour — fall back to option 1 rather than silently
          // adding the wrong-coloured variant to the cart.
          if (card.classList.contains('is-selected')) {
            selectCard(groupEl.querySelector('.addon-card[data-slot="opt1"]'));
          }
        }

        var badge = card.querySelector('.addon-card__price-badge');
        if (badge && data.price) badge.textContent = '+' + data.price;

        var img = card.querySelector('.addon-card__image');
        if (img && data.image) {
          img.removeAttribute('srcset');
          img.setAttribute('src', data.image);
        }
      });
    }

    function onVariantChange(event) {
      var id = event && event.detail && event.detail.variant && event.detail.variant.id;
      if (!id) {
        var idInput = form.querySelector('[name="id"]');
        id = idInput && idInput.value;
      }
      if (id) applyParentVariant(id);
    }

    // theme.js dispatches 'variant:change' on the product form (non-bubbling)
    // when a form exists, and on document otherwise — listen on both.
    form.addEventListener('variant:change', onVariantChange);
    document.addEventListener('variant:change', onVariantChange);
  }

  function initAll() {
    document.querySelectorAll('.addon-cards__group[data-form-id]').forEach(initAddonGroup);
  }

  // ── Hide live chat when addon info drawer is open ─────────────────────────────
  // Live chat widgets use iframes with very high z-index that CSS cannot override.
  // We hide/restore them when our drawers open/close.
  function getChatWidgets() {
    return document.querySelectorAll(
      '#chat-widget, #shopify-chat, shopify-chat, ' +
      'iframe[id*="chat"], iframe[title*="chat" i], ' +
      'iframe[src*="chat.shopify"], ' +
      '#tidio-chat-iframe, #tidio-chat-code, ' +
      '.tidio-chat, .gorgias-chat-messenger-button, ' +
      '[id*="shopify-chat"]'
    );
  }

  document.querySelectorAll('drawer-element[id^="addon-info-"]').forEach(function (drawer) {
    drawer.addEventListener('drawer:afterShow', function () {
      getChatWidgets().forEach(function (el) { el.style.display = 'none'; });
    });
    drawer.addEventListener('drawer:afterHide', function () {
      getChatWidgets().forEach(function (el) { el.style.display = ''; });
    });
  });

  // ── Bootstrap ─────────────────────────────────────────────────────────────────
  // Info drawers are handled automatically by the theme's drawer-element custom element
  // via aria-controls on the trigger button — no manual JS needed.
  initAll();
  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);

})();
